import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody, Row, Col, InputGroup, Input } from "reactstrap";
import { Button } from "react-bootstrap";
import PageContainer from "../Layout/PageContainer";
import {
  ExportExecutionResult,
  ProcessFormula,
} from "../../redux/services/formula.service";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import CustomLoaderComponent from "../../common/customLoader";
import { FaDownload } from "react-icons/fa";
import { IoMdClose, IoMdSearch } from "react-icons/io";

const ProcessFormulaPage = () => {
  const { id } = useParams();
  const [formulaData, setFormulaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [opportunityId, setOpportunityId] = useState("");
  const [talentId, setTalentId] = useState("");
  const [companyId, setCompanyId] = useState("");

  useEffect(() => {
    if (id) {
      fetchFormulaData(id, buildParams());
    }
  }, [id, page, perPage]);

  const buildParams = () => {
    const params = {
      page,
      perPage,
    };

    if (opportunityId.trim()) params.oppId = opportunityId.trim();
    if (talentId.trim()) params.talId = talentId.trim();
    if (companyId.trim()) params.companyId = companyId.trim();

    return params;
  };


  const fetchFormulaData = async (id, params) => {
    setLoading(true);
    try {
      const response = await ProcessFormula({ id }, params);

      if (!response || !response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid response format");
      }

      setFormulaData(response.data || []);
      setTotalCount(response.totalCount || 0);
      setFilteredData(response.data || []);
    } catch (error) {
      console.error("Error fetching formula data:", error);
      toast.error("An error occurred while processing the formula.", {
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchFormulaData(id, buildParams());
  };

  const handleClear = () => {
    setOpportunityId("");
    setTalentId("");
    setCompanyId("")
    fetchFormulaData(id, {
      page,
      perPage,
    });
  };

  const handleDownloadReport = async () => {
    try {
      if (!id) {
        toast.error("Invalid Formula ID.");
        return;
      }

      const params = {};
      if (opportunityId.trim()) params.oppId = opportunityId.trim();
      if (talentId.trim()) params.talId = talentId.trim();
      if (companyId.trim()) params.companyId = companyId.trim();

      const response = await ExportExecutionResult(id, params);

      if (response && response.data) {
        const fileUrl = response.data.startsWith("http")
          ? response.data
          : `https://${response.data}`;

        window.open(fileUrl, "_blank");
        toast.success(response.message || "File downloaded successfully!");
      } else {
        toast.error(response?.error || "Failed to download the report.");
      }
    } catch (error) {
      console.error("Error in downloading report:", error);
      toast.error("An error occurred while fetching the report.");
    }
  };



  const SearchComponent = useMemo(() => (
    <Row className="gx-3 gy-2 align-items-end">
      <Col md="3">
        <Input
          type="text"
          placeholder="Enter Company Id..."
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
        />
      </Col>
      <Col md="3">
        <Input
          type="text"
          placeholder="Enter Opportunity Id..."
          value={opportunityId}
          onChange={(e) => setOpportunityId(e.target.value)}
        />
      </Col>
      <Col md="3">
        <Input
          type="text"
          placeholder="Enter Talent Id..."
          value={talentId}
          onChange={(e) => setTalentId(e.target.value)}
        />
      </Col>
      <Col md="3" className="d-flex gap-2 justify-content-end mt-2">
        <Button color="secondary" onClick={handleSearch}>
          <IoMdSearch />
        </Button>
        <Button color="warning" onClick={handleClear}>
          <IoMdClose />
        </Button>
        <Button color="primary" onClick={handleDownloadReport}>
          <FaDownload className="me-1" />
          Download
        </Button>
      </Col>
    </Row>
  ), [companyId, opportunityId, talentId]);

  const columns = [
    {
      name: "Company Id",
      selector: (row) => row.companyId || "N/A",
      sortable: true,
      width: "120px",
    },
    {
      name: "Opportunity Id",
      selector: (row) => row.opportunityId || "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: "Talent Id",
      selector: (row) =>
        row.userId ? (
          <a
            href={`/talent/detail/${row.userId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.userId}
          </a>
        ) : (
          "N/A"
        ),
      sortable: true,
      width: "110px",
    },

    {
      name: "Score",
      selector: (row) => row.score || "N/A",
      sortable: true,
      width: "180px",
    },
    {
      name: "Status",
      selector: (row) => row.currentStep || "N/A",
      sortable: true,
      wrap: true,
    },
  ];

  return (
    <PageContainer
      pageTitleIcon="pe-7s-server icon-gradient bg-plum-plate"
      pageHeading="Executed Formula"
      pageSubTitle="View the executed formula result data"
    >
      {loading ? (
        <CustomLoaderComponent />
      ) : (
        <Row>
          <Col md="12">
            <Card className="main-card mb-3">
              <CardBody>
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  paginationServer
                  paginationTotalRows={totalCount}
                  paginationPerPage={perPage}
                  paginationDefaultPage={page}
                  onChangePage={(newPage) => setPage(newPage)}
                  onChangeRowsPerPage={(newPerPage, newPage) => {
                    setPerPage(newPerPage);
                    setPage(newPage);
                  }}
                  subHeader
                  subHeaderComponent={SearchComponent}
                // subHeaderComponent={<SearchComponent />}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </PageContainer>
  );
};

export default ProcessFormulaPage;
